import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LegsEditor from "@/components/LegsEditor";
import RouteMap from "@/components/RouteMap";
import { legsFor, mirrorReturn } from "@/pages/NewTrip";
import { TRIP_TYPES, isStaff } from "@/lib/format";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export default function RouteEditDialog({ trip, open, onOpenChange, onSaved }) {
  const api = useApi();
  const { me } = useMe();
  const [tripType, setTripType] = useState(trip.trip_type);
  const [legs, setLegs] = useState(trip.legs);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTripType(trip.trip_type);
      setLegs(trip.legs);
    }
  }, [open, trip]);

  const changeType = (t) => {
    setTripType(t);
    setLegs((l) => legsFor(t, l));
  };
  const onLegsChange = (next) => setLegs((prev) => (tripType === "round_trip" ? mirrorReturn(prev, next) : next));

  const save = async () => {
    if (legs.some((l) => !l.from_airport || !l.to_airport || !l.date)) return toast.error("Fill in the airports and date for every leg");
    setSaving(true);
    try {
      const { data } = await api.put(`/trips/${trip.id}/route`, { trip_type: tripType, legs: legs.map((l) => ({ ...l, pax: Number(l.pax) })) });
      toast.success(isStaff(me) ? "Route updated. Broker notified." : "Route updated. The flight department has been notified.");
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111319] border-white/10 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="route-edit-dialog">
        <DialogHeader>
          <p className="eyebrow">Edit route</p>
          <DialogTitle className="font-serif-display text-3xl font-normal text-white">Change the itinerary</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-zinc-400 -mt-1">Existing options are kept. {isStaff(me) ? "The broker" : "The flight department"} is told about the change.</p>
        <div className="flex flex-wrap gap-2">
          {TRIP_TYPES.map((t) => (
            <button key={t.value} type="button" onClick={() => changeType(t.value)} data-testid={`route-edit-type-${t.value}`}
              className={`frame-btn !py-2.5 ${tripType === t.value ? "gold" : ""}`}>{t.label}</button>
          ))}
        </div>
        <RouteMap legs={legs} />
        <LegsEditor legs={legs} onChange={onLegsChange} tripType={tripType} />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" className="frame-btn" onClick={() => onOpenChange(false)} data-testid="route-edit-cancel-btn">Cancel</button>
          <button type="button" className="frame-btn gold" onClick={save} disabled={saving} data-testid="route-edit-save-btn">{saving ? "Saving…" : "Save route"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
